import { resolveLevelDesignImagePathAsync } from './firebase/levelDesignMedia';
import { TILE_SIZE } from '../game/worldConstants';
import { isHexColor, normalizeHexColor } from '../lib/levelEditorColors';
import { normalizeLayerRenderPlane } from '../lib/levelEditorRenderPlane';
import { resolvePlacedImage } from '../lib/placedImage';
import { normalizeWorldAnnotations } from '../lib/worldAnnotations';
import { stripRuntimeStateFromAnnotations } from '../lib/worldAnnotationRuntime';
import type {
  EditorLayer,
  LayerCell,
  LevelDesignDocument,
  TraversalState,
  UploadedImageTransformSnapshot,
} from '../types/levelEditor';
import { makeTileKey } from '../stores/editorStoreHelpers';

export const UPLOADED_IMAGE_TILESET_ID = 'uploaded-image';
export const UPLOADED_IMAGE_SHEET_ID = 'uploaded-image';

const resolveStoredImagePathAsync = async (path: string): Promise<string> =>
  resolveLevelDesignImagePathAsync(path);

const toTraversalState = (value: unknown, fallback: TraversalState): TraversalState =>
  value === 'walkable' || value === 'blocked' ? value : fallback;

const toLayerCellAsync = async (value: unknown): Promise<LayerCell | null> => {
  if (!value || typeof value !== 'object') {
    return null;
  }
  const entry = value as Record<string, unknown>;
  if (entry.kind === 'color') {
    const rawColor = String(entry.color ?? '').trim();
    if (!isHexColor(rawColor)) {
      return null;
    }
    return { kind: 'color', color: normalizeHexColor(rawColor) };
  }
  const tilesetId = String(entry.tilesetId ?? '');
  const sheetId = String(entry.sheetId ?? '');
  const imagePath = (
    await resolveStoredImagePathAsync(String(entry.imagePath ?? '').trim())
  ).trim();
  const sourceX = Number(entry.sourceX);
  const sourceY = Number(entry.sourceY);
  const tileSize = Number(entry.tileSize);
  if (!tilesetId || !sheetId || !imagePath) {
    return null;
  }
  if (![sourceX, sourceY, tileSize].every((candidate) => Number.isFinite(candidate))) {
    return null;
  }
  return {
    tilesetId,
    sheetId,
    imagePath,
    sourceX,
    sourceY,
    tileSize,
  };
};

const buildUploadedImageTilesFromSnapshot = ({
  mapWidth,
  mapHeight,
  tileSize,
  snapshot,
}: {
  mapWidth: number;
  mapHeight: number;
  tileSize: number;
  snapshot: UploadedImageTransformSnapshot;
}): Record<string, LayerCell> => {
  const placement = resolvePlacedImage({
    canvasWidth: mapWidth,
    canvasHeight: mapHeight,
    tileSize,
    imageWidth: snapshot.imageWidth,
    imageHeight: snapshot.imageHeight,
    offsetX: snapshot.offsetX,
    offsetY: snapshot.offsetY,
    scalePercent: snapshot.scalePercent,
    avoidCropping: snapshot.avoidCropping,
  });

  const tiles: Record<string, LayerCell> = {};
  for (let row = 0; row < placement.rows; row += 1) {
    for (let column = 0; column < placement.columns; column += 1) {
      const x = placement.offsetX + column;
      const y = placement.offsetY + row;
      if (x < 0 || y < 0 || x >= mapWidth || y >= mapHeight) {
        continue;
      }
      const key = makeTileKey(x, y);
      tiles[key] = {
        tilesetId: UPLOADED_IMAGE_TILESET_ID,
        sheetId: UPLOADED_IMAGE_SHEET_ID,
        imagePath: snapshot.imagePath,
        sourceX: column * placement.sourceTileSize,
        sourceY: row * placement.sourceTileSize,
        tileSize: placement.sourceTileSize,
      };
    }
  }
  return tiles;
};

const toUploadedImageTransformSnapshotAsync = async (
  value: unknown,
): Promise<UploadedImageTransformSnapshot | null> => {
  if (!value || typeof value !== 'object') {
    return null;
  }
  const entry = value as Record<string, unknown>;
  const imagePath = (
    await resolveStoredImagePathAsync(String(entry.imagePath ?? '').trim())
  ).trim();
  const imageWidth = Number(entry.imageWidth);
  const imageHeight = Number(entry.imageHeight);
  const offsetX = Number(entry.offsetX);
  const offsetY = Number(entry.offsetY);
  const scalePercent = Number(entry.scalePercent);
  if (!imagePath) {
    return null;
  }
  if (
    ![imageWidth, imageHeight, offsetX, offsetY, scalePercent].every((candidate) =>
      Number.isFinite(candidate),
    )
  ) {
    return null;
  }
  return {
    imagePath,
    imageWidth: Math.max(1, Math.round(imageWidth)),
    imageHeight: Math.max(1, Math.round(imageHeight)),
    offsetX: Math.floor(offsetX),
    offsetY: Math.floor(offsetY),
    scalePercent,
    avoidCropping: entry.avoidCropping !== false,
  };
};

const toLayerAsync = async (
  value: unknown,
  index: number,
  mapWidth: number,
  mapHeight: number,
  tileSize: number,
): Promise<EditorLayer> => {
  const entry = value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
  const id = String(entry.id ?? `layer-${index}`);
  const name = String(entry.name ?? `Layer ${index + 1}`);
  const visible = entry.visible !== false;
  const locked = entry.locked === true;
  const opacity = Number(entry.opacity);
  const traversalDefault = toTraversalState(entry.traversalDefault, 'walkable');
  const layerType = entry.layerType === 'animated-gif' ? 'animated-gif' : 'tile';
  const renderPlane = normalizeLayerRenderPlane(entry.renderPlane);

  const rawTiles =
    entry.tiles && typeof entry.tiles === 'object' ? (entry.tiles as Record<string, unknown>) : {};
  const tileEntries: Array<[string, LayerCell | null]> = await Promise.all(
    Object.entries(rawTiles).map(
      async ([key, tileValue]): Promise<[string, LayerCell | null]> => [
        key,
        await toLayerCellAsync(tileValue),
      ],
    ),
  );
  const tiles: Record<string, LayerCell> = {};
  tileEntries.forEach(([key, tile]) => {
    if (!tile) {
      return;
    }
    tiles[key] = tile;
  });

  const uploadedImageSnapshot = await toUploadedImageTransformSnapshotAsync(
    entry.uploadedImageTransform,
  );
  if (Object.keys(tiles).length === 0 && uploadedImageSnapshot) {
    const rebuiltTiles = buildUploadedImageTilesFromSnapshot({
      mapWidth,
      mapHeight,
      tileSize,
      snapshot: uploadedImageSnapshot,
    });
    Object.assign(tiles, rebuiltTiles);
  }

  return {
    id,
    name,
    layerType,
    visible,
    locked,
    opacity: Number.isFinite(opacity) ? Math.min(1, Math.max(0.05, opacity)) : 1,
    traversalDefault,
    ...(renderPlane === 'foreground' ? { renderPlane } : {}),
    tiles,
    ...(uploadedImageSnapshot ? { uploadedImageTransform: uploadedImageSnapshot } : {}),
  };
};

export const toLevelDesignDocument = async (
  id: string,
  data: Record<string, unknown>,
): Promise<LevelDesignDocument> => {
  const rawLayers = Array.isArray(data.layers) ? data.layers : [];
  const parsedCanvasWidth = Number(data.canvasWidth ?? 64);
  const parsedCanvasHeight = Number(data.canvasHeight ?? 64);
  const canvasWidth = Number.isFinite(parsedCanvasWidth)
    ? Math.max(1, Math.floor(parsedCanvasWidth))
    : 64;
  const canvasHeight = Number.isFinite(parsedCanvasHeight)
    ? Math.max(1, Math.floor(parsedCanvasHeight))
    : 64;
  const traversalOverridesRaw =
    data.traversalOverrides && typeof data.traversalOverrides === 'object'
      ? (data.traversalOverrides as Record<string, unknown>)
      : {};
  const traversalOverrides: Record<string, TraversalState> = {};
  for (const [key, value] of Object.entries(traversalOverridesRaw)) {
    if (value === 'walkable' || value === 'blocked') {
      traversalOverrides[key] = value;
    }
  }

  const spawnRaw =
    data.spawnPoint && typeof data.spawnPoint === 'object'
      ? (data.spawnPoint as Record<string, unknown>)
      : {};
  const spawnX = Number(spawnRaw.x);
  const spawnY = Number(spawnRaw.y);
  const occlusionMaskUrl = (
    await resolveStoredImagePathAsync(String(data.occlusionMaskUrl ?? '').trim())
  ).trim();

  return {
    id,
    userId: String(data.userId ?? ''),
    name: String(data.name ?? 'Untitled Design'),
    tilesetPack: String(data.tilesetPack ?? 'moderninteriors-16x16'),
    canvasWidth,
    canvasHeight,
    layers: await Promise.all(
      rawLayers.map((entry, index) =>
        toLayerAsync(entry, index, canvasWidth, canvasHeight, TILE_SIZE),
      ),
    ),
    occlusionMaskUrl: occlusionMaskUrl || null,
    traversalOverrides,
    spawnPoint: {
      x: Number.isFinite(spawnX) ? spawnX : 32,
      y: Number.isFinite(spawnY) ? spawnY : 32,
    },
    annotations: stripRuntimeStateFromAnnotations(
      normalizeWorldAnnotations(Array.isArray(data.annotations) ? data.annotations : [], {
        tileWidth: canvasWidth,
        tileHeight: canvasHeight,
      }),
    ),
    createdAt:
      data.createdAt && typeof data.createdAt === 'object' && 'toDate' in data.createdAt
        ? (data.createdAt as { toDate: () => Date }).toDate()
        : null,
    updatedAt:
      data.updatedAt && typeof data.updatedAt === 'object' && 'toDate' in data.updatedAt
        ? (data.updatedAt as { toDate: () => Date }).toDate()
        : null,
  };
};
